import React, { useState, useEffect, useCallback } from 'react';
import {
  CreditCard, Search, Download, Loader2, ChevronLeft, ChevronRight,
  X, Calendar, Package, Tag, Copy, CheckCircle
} from 'lucide-react';
import { apiService, Transaction, TransactionDetail, Department } from '../services/api';
import toast from 'react-hot-toast';

/* ─── Date Preset Helpers ─── */
const today = () => {
  const d = new Date(); d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};
const daysAgo = (n: number) => {
  const d = new Date(); d.setDate(d.getDate() - n); d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};
const monthStart = (offset = 0) => {
  const d = new Date(); d.setMonth(d.getMonth() - offset, 1); d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};
const monthEnd = (offset = 0) => {
  const d = new Date(); d.setMonth(d.getMonth() - offset + 1, 0); d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};
const yearStart = () => {
  const d = new Date(); d.setMonth(0, 1); d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};

const DATE_PRESETS: { label: string; start: string; end: string }[] = [
  { label: 'Today', start: today(), end: today() },
  { label: 'Yesterday', start: daysAgo(1), end: daysAgo(1) },
  { label: 'Last 7 days', start: daysAgo(7), end: today() },
  { label: 'Last 30 days', start: daysAgo(30), end: today() },
  { label: 'Last 90 days', start: daysAgo(90), end: today() },
  { label: 'This month', start: monthStart(0), end: today() },
  { label: 'Last month', start: monthStart(1), end: monthEnd(1) },
  { label: 'Year to date', start: yearStart(), end: today() },
  { label: 'All time', start: '', end: '' },
];

/* ─── Formatting Helpers ─── */
const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount);

const formatAmount = (amount: number, type: string) => {
  const f = formatCurrency(amount);
  return type === 'refund' ? `-${f}` : f;
};

const CATEGORIZATION_STYLES: Record<string, { label: string; cls: string }> = {
  dedicated_account: { label: 'Account', cls: 'text-blue-600 bg-blue-50 ring-blue-500/20' },
  keyword_description: { label: 'Keyword', cls: 'text-purple-600 bg-purple-50 ring-purple-500/20' },
  keyword_match: { label: 'Keyword', cls: 'text-purple-600 bg-purple-50 ring-purple-500/20' },
  metadata_match: { label: 'Metadata', cls: 'text-teal-600 bg-teal-50 ring-teal-500/20' },
  chargebee_subscription: { label: 'Chargebee', cls: 'text-orange-600 bg-orange-50 ring-orange-500/20' },
  amount_match_refund: { label: 'Refund Match', cls: 'text-pink-600 bg-pink-50 ring-pink-500/20' },
  manual: { label: 'Manual', cls: 'text-green-600 bg-green-50 ring-green-500/20' },
  manual_override: { label: 'Manual', cls: 'text-green-600 bg-green-50 ring-green-500/20' },
  default: { label: 'Default', cls: 'text-gray-500 bg-gray-50 ring-gray-400/20' },
};

/* ─── Main Component ─── */
const Payments: React.FC = () => {
  const [payments, setPayments] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({
    platform: '', status: '', search: '', startDate: '', endDate: '', department: '', emailFilter: ''
  });
  const [searchInput, setSearchInput] = useState('');
  const [activeDatePreset, setActiveDatePreset] = useState('All time');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [platformOptions, setPlatformOptions] = useState<Array<{ id: string; name: string }>>([]);
  const [departmentOptions, setDepartmentOptions] = useState<Department[]>([]);

  // Detail drawer state
  const [selectedTxId, setSelectedTxId] = useState<string | null>(null);
  const [txDetail, setTxDetail] = useState<TransactionDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  /* ─── Data Loading ─── */
  useEffect(() => { loadPayments(); }, [pagination.page, pagination.limit]);
  useEffect(() => { loadOptions(); }, []);
  useEffect(() => {
    // Reset to page 1 when filters change; the page change triggers loadPayments via [pagination.page] effect
    // If already on page 1, call directly since setPagination won't cause a re-render
    if (pagination.page !== 1) {
      setPagination(prev => ({ ...prev, page: 1 }));
    } else {
      loadPayments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.platform, filters.status, filters.search, filters.startDate, filters.endDate, filters.department, filters.emailFilter]);

  const loadPayments = async () => {
    try {
      setLoading(true);
      await apiService.loadPlatforms();
      await apiService.loadDepartments();
      const response = await apiService.getTransactions(
        pagination.page, pagination.limit,
        filters.platform || undefined, filters.status || undefined,
        filters.search || undefined, filters.startDate || undefined,
        filters.endDate || undefined, filters.department || undefined,
        filters.emailFilter || undefined
      );
      setPayments(response.data);
      setPagination({ page: response.page, limit: response.pageSize, total: response.total, totalPages: response.totalPages });
    } catch (error) {
      console.error('Error loading payments:', error);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const loadOptions = async () => {
    try {
      await apiService.loadPlatforms();
      await apiService.loadDepartments();
      setPlatformOptions(apiService.getPlatforms().map(p => ({ id: p.id, name: p.account_name })));
      setDepartmentOptions(apiService.getDepartments());
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  };

  const openDetail = useCallback(async (txId: string) => {
    setSelectedTxId(txId);
    setDetailLoading(true);
    setTxDetail(null);
    try {
      const detail = await apiService.getTransactionDetail(txId);
      setTxDetail(detail);
    } catch (error) {
      console.error('Error loading transaction detail:', error);
      toast.error('Failed to load transaction details');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeDetail = () => { setSelectedTxId(null); setTxDetail(null); };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleDatePreset = (preset: { label: string; start: string; end: string }) => {
    setFilters(prev => ({ ...prev, startDate: preset.start, endDate: preset.end }));
    setActiveDatePreset(preset.label);
    if (preset.label !== 'Custom') setShowDatePicker(false);
  };

  const handlePageChange = (newPage: number) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 1500);
    });
  };

  /* ─── Badge Helpers ─── */
  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      success: 'text-green-700 bg-green-50 ring-green-600/20',
      failed: 'text-red-700 bg-red-50 ring-red-600/20',
      pending: 'text-yellow-700 bg-yellow-50 ring-yellow-600/20',
      refunded: 'text-blue-700 bg-blue-50 ring-blue-600/20',
    };
    return (
      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-md ring-1 ring-inset ${styles[status] || 'text-gray-700 bg-gray-50 ring-gray-600/20'}`}>
        {status}
      </span>
    );
  };

  const getTypeBadge = (type: string) => {
    if (type === 'refund') return <span className="text-xs text-red-600 font-medium">Refund</span>;
    if (type === 'chargeback') return <span className="text-xs text-orange-600 font-medium">Chargeback</span>;
    return <span className="text-xs text-green-600 font-medium">Payment</span>;
  };

  const getCategorizationBadge = (method?: string) => {
    if (!method) return <span className="text-[10px] text-gray-400 italic">none</span>;
    const s = CATEGORIZATION_STYLES[method] || { label: method.replace(/_/g, ' '), cls: 'text-gray-500 bg-gray-50 ring-gray-400/20' };
    return (
      <span className={`inline-flex items-center px-1.5 py-0.5 text-[10px] font-medium rounded ring-1 ring-inset ${s.cls}`}>
        {s.label}
      </span>
    );
  };

  /* ─── Loading State ─── */
  if (loading && payments.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading payments...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Transactions</h1>
          <p className="text-sm text-gray-500">All payments, refunds, and chargebacks across departments</p>
        </div>
        <button className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 flex items-center">
          <Download className="h-4 w-4 mr-2" />
          Export
        </button>
      </div>

      {/* Date Presets Bar */}
      <div className="bg-white rounded-lg border border-gray-200 p-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
          {DATE_PRESETS.map(p => (
            <button
              key={p.label}
              onClick={() => handleDatePreset(p)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                activeDatePreset === p.label
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {p.label}
            </button>
          ))}
          <button
            onClick={() => {
              setShowDatePicker(!showDatePicker);
              setActiveDatePreset('Custom');
            }}
            className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
              activeDatePreset === 'Custom'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Custom range
          </button>
        </div>

        {/* Custom Date Range */}
        {showDatePicker && (
          <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500 font-medium">From</label>
              <input
                type="date"
                className="px-3 py-1.5 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.startDate}
                onChange={(e) => {
                  handleFilterChange('startDate', e.target.value);
                  setActiveDatePreset('Custom');
                }}
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-gray-500 font-medium">To</label>
              <input
                type="date"
                className="px-3 py-1.5 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={filters.endDate}
                onChange={(e) => {
                  handleFilterChange('endDate', e.target.value);
                  setActiveDatePreset('Custom');
                }}
              />
            </div>
            {filters.startDate && filters.endDate && (
              <span className="text-xs text-gray-400">
                {Math.ceil((new Date(filters.endDate).getTime() - new Date(filters.startDate).getTime()) / (1000 * 60 * 60 * 24)) + 1} days
              </span>
            )}
          </div>
        )}
      </div>

      {/* Filters Row */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search by customer, description, or transaction ID..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleFilterChange('search', searchInput); }}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              className="px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filters.department}
              onChange={(e) => handleFilterChange('department', e.target.value)}
            >
              <option value="">All Departments</option>
              {departmentOptions.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
            <select
              className="px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filters.platform}
              onChange={(e) => handleFilterChange('platform', e.target.value)}
            >
              <option value="">All Platforms</option>
              {platformOptions.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
            <select
              className="px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">All Types</option>
              <option value="success">Successful</option>
              <option value="failed">Failed</option>
              <option value="pending">Pending</option>
              <option value="refunded">Refunds</option>
            </select>
            <select
              className="px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={filters.emailFilter}
              onChange={(e) => handleFilterChange('emailFilter', e.target.value)}
            >
              <option value="">All Emails</option>
              <option value="no_email">No Email</option>
              <option value="has_email">Has Email</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results count */}
      <p className="text-sm text-gray-500">
        {pagination.total.toLocaleString()} transactions found
        {loading && <Loader2 className="inline h-3 w-3 animate-spin ml-2" />}
      </p>

      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Transaction</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Department</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Categorized By</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Platform</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Customer</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {payments.map((tx) => (
                <tr
                  key={tx.id}
                  onClick={() => openDetail(tx.id)}
                  className={`hover:bg-blue-50/50 transition-colors cursor-pointer ${selectedTxId === tx.id ? 'bg-blue-50' : ''}`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center space-x-2">
                      <CreditCard className={`h-4 w-4 flex-shrink-0 ${
                        tx.type === 'refund' ? 'text-red-400' : tx.type === 'chargeback' ? 'text-orange-400' : 'text-green-400'
                      }`} />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                          {tx.description || tx.platform_transaction_id || 'No description'}
                        </p>
                        {getTypeBadge(tx.type)}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-700">
                      {tx.department_id ? apiService.getDepartmentName(tx.department_id) : '—'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {getCategorizationBadge(tx.categorization_method)}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600">
                      {apiService.getPlatformName(tx.platform_id)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className={`text-sm font-medium ${tx.type === 'refund' ? 'text-red-600' : 'text-gray-900'}`}>
                      {formatAmount(tx.amount, tx.type)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {getStatusBadge(tx.status)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="min-w-0">
                      <p className="text-sm text-gray-900 truncate max-w-[160px]">
                        {tx.customer_name || (tx.metadata && typeof tx.metadata === 'object' && tx.metadata.name) || '—'}
                      </p>
                      <p className="text-xs text-gray-400 truncate max-w-[160px]">
                        {tx.customer_email || ''}
                      </p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <p className="text-sm text-gray-700">{new Date(tx.created_at).toLocaleDateString()}</p>
                    <p className="text-xs text-gray-400">
                      {new Date(tx.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}
                    </p>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-sm text-gray-600">
              Page {pagination.page} of {pagination.totalPages} &middot;{' '}
              {((pagination.page - 1) * pagination.limit + 1).toLocaleString()}–{Math.min(pagination.page * pagination.limit, pagination.total).toLocaleString()} of {pagination.total.toLocaleString()}
            </p>
            <div className="flex items-center space-x-1">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="p-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              {(() => {
                const pages: (number | string)[] = [];
                const total = pagination.totalPages;
                const current = pagination.page;
                pages.push(1);
                if (total <= 7) {
                  for (let i = 2; i <= total; i++) pages.push(i);
                } else {
                  let start = Math.max(2, current - 2);
                  let end = Math.min(total - 1, current + 2);
                  if (current <= 4) end = Math.min(total - 1, 6);
                  if (current >= total - 3) start = Math.max(2, total - 5);
                  if (start > 2) pages.push('...');
                  for (let i = start; i <= end; i++) pages.push(i);
                  if (end < total - 1) pages.push('...');
                }
                if (total > 1) pages.push(total);
                return pages.map((p, i) => (
                  <button
                    key={i}
                    onClick={() => typeof p === 'number' ? handlePageChange(p) : null}
                    disabled={typeof p !== 'number'}
                    className={`px-3 py-1.5 text-sm rounded-md border ${
                      p === current
                        ? 'bg-blue-600 text-white border-blue-600'
                        : typeof p === 'number'
                        ? 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                        : 'bg-white border-gray-200 text-gray-400 cursor-default'
                    }`}
                  >
                    {p}
                  </button>
                ));
              })()}
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                className="p-1.5 rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ─── Transaction Detail Drawer ─── */}
      {selectedTxId && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/20 z-40" onClick={closeDetail} />

          {/* Drawer */}
          <div className="fixed right-0 top-0 h-full w-full max-w-lg bg-white shadow-2xl z-50 overflow-y-auto">
            {/* Drawer Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-lg font-semibold text-gray-900">Transaction Details</h2>
              <button onClick={closeDetail} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            {detailLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                <span className="ml-2 text-gray-500">Loading details...</span>
              </div>
            ) : txDetail ? (
              <div className="px-6 py-5 space-y-6">
                {/* Amount + Status Hero */}
                <div className="text-center pb-4 border-b border-gray-100">
                  <p className={`text-3xl font-bold ${txDetail.type === 'refund' ? 'text-red-600' : 'text-gray-900'}`}>
                    {formatAmount(txDetail.amount, txDetail.type)}
                  </p>
                  <div className="flex items-center justify-center gap-2 mt-2">
                    {getStatusBadge(txDetail.status)}
                    {getTypeBadge(txDetail.type)}
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    {new Date(txDetail.created_at).toLocaleDateString('en-US', {
                      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                    })}
                    {' at '}
                    {new Date(txDetail.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}
                  </p>
                </div>

                {/* Customer Info */}
                {(txDetail.customer_name || txDetail.customer_email) && (
                  <DetailSection title="Customer">
                    {txDetail.customer_name && (
                      <DetailRow label="Name" value={txDetail.customer_name} />
                    )}
                    {txDetail.customer_email && (
                      <DetailRow label="Email" value={txDetail.customer_email} copyable onCopy={handleCopy} copiedField={copiedField} />
                    )}
                  </DetailSection>
                )}

                {/* Transaction Info */}
                <DetailSection title="Transaction Info">
                  <DetailRow label="Description" value={txDetail.description || '—'} />
                  <DetailRow
                    label="Transaction ID"
                    value={txDetail.platform_transaction_id || '—'}
                    copyable
                    onCopy={handleCopy}
                    copiedField={copiedField}
                    mono
                  />
                  <DetailRow label="Internal ID" value={txDetail.id} copyable onCopy={handleCopy} copiedField={copiedField} mono />
                  {txDetail.is_duplicate && (
                    <div className="flex items-center gap-2 px-3 py-2 bg-yellow-50 rounded-md text-xs text-yellow-700">
                      <span className="font-medium">Duplicate</span>
                      {txDetail.duplicate_reason && <span>— {txDetail.duplicate_reason}</span>}
                    </div>
                  )}
                </DetailSection>

                {/* Platform + Department */}
                <DetailSection title="Routing">
                  <DetailRow
                    label="Platform"
                    value={
                      txDetail.platform_detail
                        ? `${txDetail.platform_detail.account_name} (${txDetail.platform_detail.platform_type})`
                        : apiService.getPlatformName(txDetail.platform_id)
                    }
                  />
                  {txDetail.platform_detail?.account_id && (
                    <DetailRow label="Account ID" value={txDetail.platform_detail.account_id} mono />
                  )}
                  <DetailRow
                    label="Department"
                    value={txDetail.department_detail?.name || '—'}
                  />
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-xs text-gray-500">Categorization</span>
                    <div className="flex items-center gap-2">
                      {getCategorizationBadge(txDetail.categorization_method)}
                    </div>
                  </div>
                  {txDetail.categorization_rule && (
                    <div className="bg-gray-50 rounded-md p-3 text-xs space-y-1">
                      <p className="text-gray-500 font-medium">Matched Rule (Priority {txDetail.categorization_rule.priority})</p>
                      <p className="text-gray-700">
                        <span className="text-gray-500">{txDetail.categorization_rule.match_field}</span>{' '}
                        <span className="text-gray-400">{txDetail.categorization_rule.match_operator}</span>{' '}
                        <code className="bg-white px-1.5 py-0.5 rounded text-gray-800 font-mono">
                          {txDetail.categorization_rule.match_value}
                        </code>
                      </p>
                      {txDetail.categorization_rule.description && (
                        <p className="text-gray-400">{txDetail.categorization_rule.description}</p>
                      )}
                    </div>
                  )}
                </DetailSection>

                {/* Line Items */}
                {txDetail.line_items && txDetail.line_items.length > 0 && (
                  <DetailSection title={`Line Items (${txDetail.line_items.length})`} icon={<Package className="h-4 w-4 text-gray-400" />}>
                    <div className="space-y-2">
                      {txDetail.line_items.map((item, idx) => (
                        <div key={item.id || idx} className="bg-gray-50 rounded-lg p-3">
                          <div className="flex justify-between items-start">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                {item.sku && (
                                  <span className="text-[10px] font-mono text-gray-400 bg-white px-1.5 py-0.5 rounded">
                                    SKU: {item.sku}
                                  </span>
                                )}
                                <span className="text-[10px] text-gray-400 capitalize">{item.type}</span>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0 ml-3">
                              <p className="text-sm font-medium text-gray-900">{formatCurrency(item.total_price)}</p>
                              {item.quantity > 1 && (
                                <p className="text-[10px] text-gray-400">
                                  {item.quantity} × {formatCurrency(item.unit_price)}
                                </p>
                              )}
                            </div>
                          </div>
                          {item.discount > 0 && (
                            <p className="text-[10px] text-green-600 mt-1">
                              Discount: -{formatCurrency(item.discount)}
                            </p>
                          )}
                          {item.metadata && Object.keys(item.metadata).length > 0 && (
                            <div className="mt-2 pt-2 border-t border-gray-200">
                              <p className="text-[10px] text-gray-400 font-medium mb-1">Item Metadata</p>
                              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                                {Object.entries(item.metadata).map(([k, v]) => (
                                  <div key={k} className="flex justify-between">
                                    <span className="text-[10px] text-gray-400">{k}</span>
                                    <span className="text-[10px] text-gray-600 font-mono truncate max-w-[120px]">
                                      {String(v)}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}

                      {/* Line items total */}
                      <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                        <span className="text-xs font-medium text-gray-500">Line Items Total</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {formatCurrency(txDetail.line_items.reduce((sum, li) => sum + li.total_price, 0))}
                        </span>
                      </div>
                    </div>
                  </DetailSection>
                )}

                {txDetail.line_items && txDetail.line_items.length === 0 && (
                  <DetailSection title="Line Items" icon={<Package className="h-4 w-4 text-gray-400" />}>
                    <p className="text-xs text-gray-400 italic py-2">
                      {txDetail.line_items_synced
                        ? 'No line items found for this transaction'
                        : 'Line items not yet enriched — run enrichment from Platforms page'}
                    </p>
                  </DetailSection>
                )}

                {/* Raw Metadata */}
                {txDetail.metadata && Object.keys(txDetail.metadata).length > 0 && (
                  <DetailSection title="Raw Metadata">
                    <div className="bg-gray-50 rounded-md p-3 max-h-60 overflow-y-auto">
                      <pre className="text-[11px] text-gray-600 font-mono whitespace-pre-wrap break-all">
                        {JSON.stringify(txDetail.metadata, null, 2)}
                      </pre>
                    </div>
                  </DetailSection>
                )}

                {/* Enrichment Info */}
                {txDetail.enrichment_source && (
                  <DetailSection title="Enrichment">
                    <DetailRow label="Source" value={txDetail.enrichment_source} />
                    <DetailRow label="Line Items Synced" value={txDetail.line_items_synced ? 'Yes' : 'No'} />
                  </DetailSection>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-400">
                <p>Failed to load transaction details</p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

/* ─── Reusable Detail Sub-Components ─── */
const DetailSection: React.FC<{ title: string; icon?: React.ReactNode; children: React.ReactNode }> = ({ title, icon, children }) => (
  <div>
    <div className="flex items-center gap-2 mb-2">
      {icon || <Tag className="h-4 w-4 text-gray-400" />}
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{title}</h3>
    </div>
    <div className="space-y-1">{children}</div>
  </div>
);

const DetailRow: React.FC<{
  label: string;
  value: string;
  copyable?: boolean;
  onCopy?: (text: string, field: string) => void;
  copiedField?: string | null;
  mono?: boolean;
}> = ({ label, value, copyable, onCopy, copiedField, mono }) => (
  <div className="flex items-center justify-between py-1.5">
    <span className="text-xs text-gray-500">{label}</span>
    <div className="flex items-center gap-1.5 max-w-[60%]">
      <span className={`text-sm text-gray-900 truncate ${mono ? 'font-mono text-xs' : ''}`}>
        {value}
      </span>
      {copyable && onCopy && value !== '—' && (
        <button
          onClick={(e) => { e.stopPropagation(); onCopy(value, label); }}
          className="p-0.5 hover:bg-gray-100 rounded text-gray-300 hover:text-gray-500 flex-shrink-0"
          title="Copy"
        >
          {copiedField === label ? (
            <CheckCircle className="h-3 w-3 text-green-500" />
          ) : (
            <Copy className="h-3 w-3" />
          )}
        </button>
      )}
    </div>
  </div>
);

export default Payments;
